import { graphql } from 'react-relay';
import { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import type {
	sendContentAgeAnalyticsGetContentAgeQuery$variables as GetContentAgeQueryVariables,
	sendContentAgeAnalyticsGetContentAgeQuery$data as GetContentAgeQueryRes,
} from './__generated__/sendContentAgeAnalyticsGetContentAgeQuery.graphql.ts';

type SendContentAgeAnalyticsParamsType = {
	contentId: string;
	collabService: string;
};

/**
 * Analytics event that informs about the age of pages/blogs being edited.
 */
export const sendContentAgeAnalytics = async (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ contentId, collabService }: SendContentAgeAnalyticsParamsType,
): Promise<void> => {
	if (!createAnalyticsEvent || !contentId) {
		return;
	}

	try {
		const result = await queryInterop<GetContentAgeQueryRes, GetContentAgeQueryVariables>({
			query: graphql`
				query sendContentAgeAnalyticsGetContentAgeQuery($contentId: ID!) {
					content(id: $contentId, status: ["current", "draft"]) {
						nodes {
							id
							history {
								createdDate
							}
							version {
								when
							}
						}
					}
				}
			`,
			variables: {
				contentId,
			},
			fetchPolicy: 'cache-first',
		});
		let utcCreatedDate = result.data?.content?.nodes?.[0]?.history?.createdDate;
		let isUnpublishedDraft = false;

		if (!utcCreatedDate) {
			if (!!result.data?.content?.nodes?.[0]?.version?.when) {
				// unpublished draft, so use draft creation date
				utcCreatedDate = result.data?.content?.nodes?.[0]?.version?.when;
				isUnpublishedDraft = true;
			} else {
				return;
			}
		}

		const { shard, edition } = await getSessionData();

		// get created and current date in UTC ms time
		const createdDate = new Date(utcCreatedDate).getTime();
		const currentDate = Date.now();

		// number of days between this (latest) edit and page creation
		const daysSinceCreated = Math.floor((currentDate - createdDate) / (1000 * 60 * 60 * 24));

		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				actionSubject: 'editor',
				action: 'trackPageLifetime',
				source: 'editorComponent',
				attributes: {
					contentId,
					daysSinceCreated,
					collabService,
					isUnpublishedDraft,
					shard,
					edition,
				},
			},
		}).fire();
	} catch {
		return;
	}
};

export const useSendContentAgeAnalytics = ({
	contentId,
	collabService,
}: SendContentAgeAnalyticsParamsType): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		void sendContentAgeAnalytics(createAnalyticsEvent, {
			contentId,
			collabService,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId]);
};
